import { TrendingUp, Users2, Globe } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { client } from '@/sanity/client';
import { useNavigate } from 'react-router-dom';

const StatsSection = () => {
	const [inView, setInView] = useState(false);
	const sectionRef = useRef(null);
	const navigate = useNavigate();
	const [serviceSlug, setServiceSlug] = useState<string | null>(null);
	const consultationServiceId = '7c020c4f-3eaf-4b59-9ecb-3655b9d5d5a2';

	useEffect(() => {
		// Fetch the slug for the consultation service
		const fetchServiceSlug = async () => {
			try {
				const service = await client.fetch(
					`*[_id == $id][0]{
                        "slug": slug.current
                    }`,
					{ id: consultationServiceId },
				);

				if (service && service.slug) {
					setServiceSlug(service.slug);
				}
			} catch (error) {
				console.error('Error fetching consultation service:', error);
			}
		};

		fetchServiceSlug();
	}, []);

	const handleClick = () => {
		if (serviceSlug) {
			navigate(`/services/${serviceSlug}`);
		} else {
			// Fallback to services page if we couldn't get the specific slug
			navigate('/services');
		}
	};

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setInView(true);
					observer.disconnect();
				}
			},
			{ threshold: 0.3 },
		);

		if (sectionRef.current) {
			observer.observe(sectionRef.current);
		}

		return () => {
			observer.disconnect();
		};
	}, []);

	const AnimatedCounter = ({ end, duration = 1.5, suffix = '' }) => {
		const [count, setCount] = useState(0);

		useEffect(() => {
			if (!inView) return;

			let start = 0;
			const incrementTime = (duration * 1000) / end;

			const timer = setInterval(() => {
				start += 1;
				setCount(start);
				if (start >= end) clearInterval(timer);
			}, incrementTime);

			return () => clearInterval(timer);
		}, [end, duration, inView]);

		return (
			<>
				{count}
				{suffix}
			</>
		);
	};

	return (
		<section
			ref={sectionRef}
			className='flex flex-col justify-center  px-22 py-14 w-full rounded-none max-md:px-5 max-md:max-w-full bg-gray-100'
		>
			<div className='grid md:grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center md:px-26'>
				{/* Left Column */}
				<div className='space-y-6 animate-fade-in'>
					<header className='text-neutral-800 w-full lg:w-[427px]'>
						<h1 className='text-4xl font-bold leading-[40px] max-md:text-[30px] max-md:leading-[40px]'>
							Proven track record of excellence
						</h1>
						<h2 className='mt-4 md:mt-6 text-xl md:text-2xl leading-snug'>
							With over 525+ clients coached, 390+ coaching hours delivered, and
							a presence in 13+ cities worldwide, and growing.
						</h2>
					</header>
					<button
						className='self-stretch bg-white flex min-w-60 items-center gap-4 md:gap-6 text-base text-neutral-800 font-extrabold text-center leading-none my-auto pl-4 md:pl-6 pr-2 py-2 rounded-[100px]'
						onClick={handleClick}
					>
						<span>Book a consultation</span>
						<div className='bg-[#FBB040] rounded-full w-10 h-10 md:w-12 md:h-12 flex items-center justify-center'>
							<svg
								xmlns='http://www.w3.org/2000/svg'
								width='16'
								height='16'
								viewBox='0 0 24 24'
								fill='none'
								stroke='currentColor'
								strokeWidth='3'
								strokeLinecap='round'
								strokeLinejoin='round'
								className='md:w-[18px] md:h-[18px]'
							>
								<path d='M5 12h14M12 5l7 7-7 7' />
							</svg>
						</div>
					</button>

					{/* Graph - Hide on very small screens */}
					<div className='mt-8 relative h-24 md:h-32 hidden sm:block'>
						<svg className='w-full h-full' viewBox='0 0 400 100'>
							<path
								d='M0 80 Q 50 70, 100 65 T 200 55 T 300 35 T 400 10'
								fill='none'
								stroke='#FBB040'
								strokeWidth='2'
								className='animate-draw'
							/>
							<defs>
								<linearGradient id='gradient' x1='0%' y1='0%' x2='0%' y2='100%'>
									<stop offset='0%' stopColor='#ef444422' />
									<stop offset='100%' stopColor='#ef444400' />
								</linearGradient>
							</defs>
							<path
								d='M0 80 Q 50 70, 100 65 T 200 55 T 300 35 T 400 10 L 400 100 L 0 100 Z'
								fill='url(#gradient)'
							/>
						</svg>
					</div>
				</div>

				{/* Right Column */}
				<div className='grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8 mt-4 md:mt-0'>
					{/* Large Stat */}
					<motion.div
						className='sm:col-span-2 p-6 md:p-8 rounded-2xl bg-white/50 backdrop-blur-sm'
						initial={{ opacity: 0, y: 50 }}
						animate={inView ? { opacity: 1, y: 0 } : {}}
						transition={{ duration: 0.5, delay: 0.1 }}
					>
						<div className='flex items-center gap-4'>
							<Users2 className='w-6 h-6 md:w-8 md:h-8' />
							<div>
								<div className='text-4xl md:text-7xl font-semibold text-[#0C1518]'>
									{inView ? <AnimatedCounter end={525} suffix='' /> : '0'}
								</div>
								<div className='text-gray-600 mt-1 text-sm md:text-base'>
									Global clients coached
								</div>
							</div>
						</div>
					</motion.div>

					{/* Other Stats */}
					{[
						{
							icon: <TrendingUp className='w-5 h-5 md:w-6 md:h-6' />,
							value: 390,
							suffix: '+',
							label: 'Total hours coached',
							delay: 0.2,
						},
						{
							icon: <Globe className='w-5 h-5 md:w-6 md:h-6' />,
							value: 13,
							suffix: '+',
							label: 'Cities we’ve coached in',
							delay: 0.3,
						},
						// {
						// 	icon: <Clock className='w-5 h-5 md:w-6 md:h-6' />,
						// 	value: 5,
						// 	suffix: '+',
						// 	label: 'Years of Experience',
						// 	delay: 0.4,
						// },
					].map((stat, index) => (
						<motion.div
							key={index}
							className='p-5 md:p-6 rounded-xl bg-white/50 backdrop-blur-sm'
							initial={{ opacity: 0, y: 50 }}
							animate={inView ? { opacity: 1, y: 0 } : {}}
							transition={{ duration: 0.5, delay: stat.delay }}
						>
							<div className='flex items-center gap-3 mb-2 md:mb-3'>
								{stat.icon}
							</div>
							<div className='text-2xl md:text-3xl font-semibold text-[#0C1518]'>
								{inView ? (
									<AnimatedCounter end={stat.value} suffix={stat.suffix} />
								) : (
									'0'
								)}
							</div>
							<div className='text-gray-600 mt-1 text-xs md:text-sm'>
								{stat.label}
							</div>
						</motion.div>
					))}
				</div>
			</div>
		</section>
	);
};

export default StatsSection;
